import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import type { ModalElement } from '@hivebrite/design-system/Dialog';

import type { FiltersBlocks } from 'hb-react/frontoffice/apps/Filters/types/filter';
import { useAmplitude } from 'hb-react/shared/hooks/useAmplitude';

import Item from '../Item';

interface FiltersBlockProps {
  featureName?: string;
  filters: FiltersBlocks;
  fullWidth: boolean;
  handleLocationFilterChange?: (value: string) => void;
  modalRef?: React.RefObject<ModalElement>;
}

const FiltersBlock = ({
  featureName = 'PeopleDirectory',
  filters,
  fullWidth,
  handleLocationFilterChange,
  modalRef,
}: FiltersBlockProps) => {
  const { trackEvent } = useAmplitude();

  const handleFocusTrack = (_, filter) =>
    trackEvent(`${featureName}|User selects a field`, filter);

  const handleOnChange = (_, filter) =>
    trackEvent(`${featureName}|User makes change on field`, filter);

  const handleBlurTrack = (_, filter) =>
    trackEvent(`${featureName}|User de-selects a field`, filter);

  return filters.flatMap((block, index) => {
    if (!block?.filters?.length) {
      return [];
    }

    return (
      <Block
        key={block.block_title}
        mt={block.block_title && index !== 0 ? 8 : undefined}
        data-testid="filter-box"
      >
        <Headline>
          {block.block_title && <Title>{block.block_title}</Title>}
          <Line mt={block.block_title ? 3 : 7} />
        </Headline>
        <Items
          maxWidth={fullWidth ? '100%' : { _: '100%', m: 418, l: 418, xl: 418 }}
        >
          {block.filters.map((filter) => (
            <Item
              key={filter.name}
              filter={filter}
              handleLocationFilterChange={handleLocationFilterChange}
              ariaLabel={filter.label}
              onFocus={(event) => handleFocusTrack(event, filter)}
              onChange={(event) => handleOnChange(event, filter)}
              onBlur={(event) => handleBlurTrack(event, filter)}
              // Needed for the ReactSelect options to be displayed in the modal
              menuPortalTarget={modalRef?.current?.getModalElement()}
              menuPosition="fixed"
              // Same, but for some inputs, we need to pass them through the inputHtml prop
              inputHtml={{
                menuPortalTarget: modalRef?.current?.getModalElement(),
                menuPosition: 'fixed',
              }}
            />
          ))}
        </Items>
      </Block>
    );
  });
};

export default FiltersBlock;

const Block = styled(Box).attrs(() => ({
  display: 'block',
}))``;

const Headline = styled(Box).attrs(() => ({
  flexDirection: 'column',
  mb: 6,
  ml: { _: 0, m: 0 },
  mr: { _: 0, m: 0 },
  pl: 0,
  pr: 0,
}))``;

const Title = styled(Box).attrs(() => ({
  font: 'h3-regular',
  as: 'h3',
}))``;

const Line = styled(Box).attrs(() => ({
  height: 1,
  bg: 'horizon',
  minWidth: '100%',
}))``;

const Items = styled(Box).attrs(() => ({
  pt: 0,
  display: 'block',
}))`
  transition: all 0.2s;
`;
